import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Alert, Badge, Button, Card, CardHeader, EmptyState, Metric, ProgressBar, Skeleton } from "@opendate-app/setlist";

const styles = {
  body: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },
  score: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    maxWidth: 480,
  },
  factors: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
  },
  factor: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "12px 0",
    borderTop: "1px solid var(--sl-border-secondary)",
  },
  factorText: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    color: "var(--sl-text-primary)",
    fontSize: 14,
  },
};

const GRADES = {
  excellent: { label: "Excellent", tone: "success", color: "success" },
  good: { label: "Good", tone: "success", color: "success" },
  fair: { label: "Fair", tone: "warning", color: "warning" },
  poor: { label: "Poor", tone: "error", color: "error" },
};

const FACTOR_STATUSES = {
  ok: { label: "Healthy", color: "success", linkText: "Learn more" },
  warning: { label: "Needs attention", color: "warning", linkText: "Learn how to fix this" },
  critical: { label: "Critical", color: "error", linkText: "Learn how to fix this" },
};

const UNKNOWN_STATUS = { label: "Unknown", color: "gray", linkText: "Learn more" };

const header = (
  <CardHeader
    title="Email health"
    supportingText="How inbox providers are likely to see your team's email, based on its recent sending."
  />
);

const Score = ({ report }) => {
  const grade = GRADES[report.grade];

  if (!report.enough_volume || !grade) {
    return (
      <EmptyState
        size="sm"
        title="Not enough sending data yet"
        description={`Your score appears once your team has delivered enough email in the last ${report.window_days} days.`}
      />
    );
  }

  return (
    <div style={styles.score}>
      <Metric
        label="Health score"
        value={(
          <span>
            {report.score}
            {" "}
            <Badge color={grade.color}>{grade.label}</Badge>
          </span>
        )}
        chart={(
          <ProgressBar
            aria-label="Health score"
            value={report.score}
            tone={grade.tone}
            format={(value) => `${Math.round(value)} / 100`}
            label="right"
          />
        )}
      />
    </div>
  );
};

const Factor = ({ factor, helpCenterEnabled }) => {
  const status = FACTOR_STATUSES[factor.status] || UNKNOWN_STATUS;
  const messageId = `email-health-factor-${factor.key}`;

  return (
    <li style={styles.factor} data-factor={factor.key}>
      <Badge color={status.color} dot>{status.label}</Badge>
      <div style={styles.factorText}>
        <span id={messageId}>{factor.message}</span>
        {helpCenterEnabled && factor.article_path ? (
          <div>
            <Button
              as="a"
              variant="link-color"
              href={`/user_manual#${factor.article_path}`}
              aria-describedby={messageId}
            >
              {status.linkText}
            </Button>
          </div>
        ) : null}
      </div>
    </li>
  );
};

const EmailHealthCard = ({ teamId, helpCenterEnabled }) => {
  const [report, setReport] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let mounted = true;

    axios.get(`/teams/${teamId}/email_health`)
      .then(({ data }) => {
        if (mounted) {
          setReport(data);
        }
      })
      .catch(() => {
        if (mounted) {
          setFailed(true);
        }
      });

    return () => {
      mounted = false;
    };
  }, [teamId]);

  if (failed) {
    return (
      <Alert
        color="error"
        role="alert"
        size="full-width"
        title="We couldn't load your email health"
        description="Refresh the page to try again."
      />
    );
  }

  if (!report) {
    return (
      <Card header={header}>
        <Skeleton shape="text" lines={3} label="Loading email health" />
      </Card>
    );
  }

  return (
    <Card header={header}>
      <div style={styles.body}>
        <Score report={report} />
        {report.factors.length > 0 ? (
          <ul style={styles.factors} aria-label="Email health factors">
            {report.factors.map((factor) => (
              <Factor key={factor.key} factor={factor} helpCenterEnabled={helpCenterEnabled} />
            ))}
          </ul>
        ) : null}
      </div>
    </Card>
  );
};

EmailHealthCard.propTypes = {
  teamId: PropTypes.number.isRequired,
  helpCenterEnabled: PropTypes.bool,
};

EmailHealthCard.defaultProps = {
  helpCenterEnabled: false,
};

export default EmailHealthCard;
